import { useState } from 'react';
import { Button, Divider, Flex, Loader, ScrollArea, Stack, Text, TextInput } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { DocumentSigningRoleType } from '@dealstack/shared/lib/generated';
import type {
	BulkClaimOnBehalfPreviewRes,
	BulkClaimOnBehalfPreviewRoleRes,
} from '@dealstack/shared/lib/generated';
import type { InvestorKey } from './BulkClaimOnBehalf.types';
import { investorKey } from './BulkClaimOnBehalf.utils';
import { BulkClaimOnBehalfRoleSection } from './BulkClaimOnBehalfRoleSection';

interface Props {
	preview: BulkClaimOnBehalfPreviewRes | null;
	isLoadingPreview: boolean;
	isSubmitting: boolean;
	onConfirm: (signerIds: string[]) => void;
	onClose: () => void;
}

export const BulkClaimOnBehalfPreview = ({
	preview,
	isLoadingPreview,
	isSubmitting,
	onConfirm,
	onClose,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	// Tracks only the investors the user has explicitly unchecked. The default (empty set) means
	// every investor is checked, and this stays correct even when `preview` arrives asynchronously
	// after mount (checked state is derived below, not seeded once at mount).
	const [unchecked, setUnchecked] = useState<Set<InvestorKey>>(new Set());
	// Roles the user has expanded. Empty by default => every section starts collapsed.
	const [expandedRoles, setExpandedRoles] = useState<Set<DocumentSigningRoleType>>(new Set());
	const [searchQuery, setSearchQuery] = useState('');

	const roles = preview?.roles ?? [];
	const allEntries = roles.flatMap((role) =>
		role.signers.map((signer) => ({ roleType: role.roleType, ...signer }))
	);

	const isChecked = (roleType: DocumentSigningRoleType, investorId: string) =>
		!unchecked.has(investorKey(roleType, investorId));

	const selectedEntries = allEntries.filter((entry) =>
		isChecked(entry.roleType, entry.investorId)
	);
	const selectedSignerIds = selectedEntries.flatMap((entry) => entry.signerIds);
	const selectedSignerCount = selectedEntries.length;
	const selectedDocumentCount = selectedSignerIds.length;

	const normalizedQuery = searchQuery.trim().toLowerCase();
	// While searching, force every (still-visible) section open so matches are never hidden behind a
	// collapsed header.
	const isExpanded = (roleType: DocumentSigningRoleType) =>
		normalizedQuery.length > 0 || expandedRoles.has(roleType);

	const toggleExpand = (roleType: DocumentSigningRoleType) => {
		setExpandedRoles((prev) => {
			const next = new Set(prev);
			if (next.has(roleType)) {
				next.delete(roleType);
			} else {
				next.add(roleType);
			}
			return next;
		});
	};

	const toggleInvestor = (roleType: DocumentSigningRoleType, investorId: string) => {
		setUnchecked((prev) => {
			const next = new Set(prev);
			const investorEntryKey = investorKey(roleType, investorId);
			if (next.has(investorEntryKey)) {
				next.delete(investorEntryKey);
			} else {
				next.add(investorEntryKey);
			}
			return next;
		});
	};

	const toggleRole = (role: BulkClaimOnBehalfPreviewRoleRes) => {
		const roleKeys = role.signers.map((signer) =>
			investorKey(role.roleType, signer.investorId)
		);
		const allChecked = roleKeys.every((roleKey) => !unchecked.has(roleKey));

		setUnchecked((prev) => {
			const next = new Set(prev);
			if (allChecked) {
				roleKeys.forEach((roleKey) => next.add(roleKey));
			} else {
				roleKeys.forEach((roleKey) => next.delete(roleKey));
			}
			return next;
		});
	};

	if (isLoadingPreview || !preview) {
		return (
			<Flex justify="center" align="center" p="xl">
				<Loader />
			</Flex>
		);
	}

	const confirmDisabled =
		selectedSignerIds.length === 0 || preview.totalClaimable === 0 || isSubmitting;

	return (
		<Flex direction="column" gap="md" p="md">
			<TextInput
				placeholder={tMipManage('documents_page.claim_on_behalf.search_placeholder')}
				leftSection={<Icon variant="search" size="sm" />}
				value={searchQuery}
				onChange={(event) => setSearchQuery(event.currentTarget.value)}
			/>

			<ScrollArea.Autosize mah={400}>
				<Stack gap="lg" pr="md">
					{preview.roles.map((role) => (
						<BulkClaimOnBehalfRoleSection
							key={role.roleType}
							role={role}
							expanded={isExpanded(role.roleType)}
							normalizedQuery={normalizedQuery}
							unchecked={unchecked}
							onToggleExpand={toggleExpand}
							onToggleRole={toggleRole}
							onToggleInvestor={toggleInvestor}
						/>
					))}
				</Stack>
			</ScrollArea.Autosize>

			<Divider />

			<Stack gap="xs">
				<Flex align="center" gap="xs">
					<Icon variant="checkmark-circle" size="lg" />
					<Text size="sm">
						{tMipManage('documents_page.claim_on_behalf.selection_summary', {
							signerCount: selectedSignerCount,
							documentCount: selectedDocumentCount,
						})}
					</Text>
				</Flex>

				{preview.totalSkipped > 0 && (
					<Flex align="flex-start" gap="xs">
						<Icon variant="warning" size="lg" />
						<Text size="sm" c="dimmed">
							{tMipManage('documents_page.claim_on_behalf.skipped_note', {
								count: preview.totalSkipped,
							})}
						</Text>
					</Flex>
				)}
			</Stack>

			<Flex gap="sm" justify="flex-end">
				<Button variant="subtle" onClick={onClose} disabled={isSubmitting}>
					{tMipManage('documents_page.claim_on_behalf.cancel_button')}
				</Button>
				<Button
					variant="filled"
					disabled={confirmDisabled}
					loading={isSubmitting}
					onClick={() => onConfirm(selectedSignerIds)}
				>
					{selectedSignerCount > 0
						? tMipManage('documents_page.claim_on_behalf.confirm_button_with_count', {
								count: selectedSignerCount,
							})
						: tMipManage('documents_page.claim_on_behalf.confirm_button')}
				</Button>
			</Flex>
		</Flex>
	);
};
